import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';

interface RealtimeStatusProps {
  isConnected: boolean;
  authError: string | null;
  onReconnect: () => void;
  retryCount?: number;
  tenantSchema?: string | null;
  isTransitioning?: boolean;
  dataQualityScore?: number;
  poorQualityCount?: number;
  onForceRestart?: () => void;
  connectionErrors?: string[];
  lastSuccessfulConnection?: number;
  fallbackPollingActive?: boolean;
}

export function RealtimeStatus({ 
  isConnected, 
  authError, 
  onReconnect, 
  retryCount = 0, 
  tenantSchema, 
  isTransitioning = false,
  dataQualityScore = 100,
  poorQualityCount = 0,
  onForceRestart,
  connectionErrors = [],
  lastSuccessfulConnection = Date.now(),
  fallbackPollingActive = false
}: RealtimeStatusProps) {
  const { t } = useTranslation('conversations');
  if (authError === 'Signed out') {
    return (
      <Badge variant="secondary" className="gap-2">
        <AlertTriangle className="h-3 w-3" />
        Not connected
      </Badge>
    );
  }

  if (authError === 'Token expired') {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="gap-2">
          <WifiOff className="h-3 w-3" />
          Auth expired
        </Badge>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onReconnect}
          className="h-6 px-2 text-xs"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Reconnect
        </Button>
      </div>
    );
  }

  // Don't show fallback mode if we're just transitioning between conversations
  if (isTransitioning) {
    return (
      <Badge variant="secondary" className="gap-2">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Connecting...
      </Badge>
    );
  }

  // Show data quality warning if poor
  const showQualityWarning = dataQualityScore < 80 || poorQualityCount > 0;
  
  // Connection health indicators
  const hasConnectionIssues = connectionErrors.length > 0;
  const timeSinceLastConnection = Date.now() - lastSuccessfulConnection;
  const isStale = timeSinceLastConnection > 300000; // 5 minutes
  const hasRepeatedErrors = retryCount >= 2 || connectionErrors.length >= 2;
  
  // Determine status variant and message
  const getStatusInfo = () => {
    if (isConnected) {
      return { variant: "secondary" as const, icon: Wifi, text: t('realtimeStatus.connected') };
    }

    if (fallbackPollingActive) {
      return { variant: "secondary" as const, icon: RefreshCw, text: t('realtimeStatus.polling') };
    }

    if (hasConnectionIssues) {
      return { variant: "destructive" as const, icon: AlertTriangle, text: t('realtimeStatus.blocked') };
    }

    return { variant: "secondary" as const, icon: WifiOff, text: t('realtimeStatus.fallback') };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="flex items-center gap-2">
      {/* Hide t('realtimeStatus.connected') status badge when connection is healthy */}
      {!isConnected && (
        <Badge variant={statusInfo.variant} className="gap-2">
          <statusInfo.icon className={`h-3 w-3 ${fallbackPollingActive ? 'animate-spin' : ''}`} />
          {statusInfo.text}
        </Badge>
      )}
      
      {/* Show quality score if degraded */}
      {showQualityWarning && (
        <Badge variant="outline" className="gap-1 text-xs">
          <AlertTriangle className="h-3 w-3" />
          Quality: {dataQualityScore}%
        </Badge>
      )}
      
      {/* Show connection health warnings */}
      {hasConnectionIssues && (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertTriangle className="h-3 w-3" />
          {connectionErrors.length} errors
        </Badge>
      )}
      
      {/* Show stale connection warning */}
      {isStale && !isConnected && (
        <Badge variant="outline" className="gap-1 text-xs">
          <WifiOff className="h-3 w-3" />
          {Math.round(timeSinceLastConnection / 60000)}m ago
        </Badge>
      )}
      
      {/* Connection actions and diagnostics */}
      {(!isConnected || retryCount > 0 || !tenantSchema || showQualityWarning || hasConnectionIssues) && (
        <div className="flex gap-1">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onReconnect}
            className="h-6 px-2 text-xs"
            title={getDebugInfo()}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {hasConnectionIssues ? 'Retry' : 'Reconnect'}
          </Button>
          
          {/* Force restart button for serious issues - now more visible */}
          {(showQualityWarning || poorQualityCount > 0 || hasConnectionIssues || hasRepeatedErrors) && onForceRestart && (
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={onForceRestart}
              className="h-6 px-2 text-xs"
              title={getRestartInfo()}
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Force Restart
            </Button>
          )}
        </div>
      )}
    </div>
  );
  
  // Helper functions for debug info
  function getDebugInfo() {
    const info = [
      `Schema: ${tenantSchema || 'none'}`,
      `Retries: ${retryCount}`,
      `Last connected: ${Math.round(timeSinceLastConnection / 1000)}s ago`
    ];
    
    if (hasConnectionIssues) {
      info.push(`Errors: ${connectionErrors.length}`);
      info.push(`Latest: ${connectionErrors[connectionErrors.length - 1]?.substring(0, 50)}...`);
    }
    
    return info.join('\n');
  }
  
  function getRestartInfo() {
    const issues = [];
    
    if (showQualityWarning) {
      issues.push(`Data quality: ${dataQualityScore}%, ${poorQualityCount} errors`);
    }
    
    if (hasConnectionIssues) {
      issues.push(`Connection blocked by browser or network`);
      issues.push(`Try: disable extensions, use incognito mode`);
    }
    
    return issues.join('\n');
  }
}